import { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import ConsultoraModal from "../components/ConsultoraModal";

/**
 * SearchPage — public search results for `/busca?q=<term>`.
 *
 * Backend (`/api/products?search=...`) runs the M242 smart-search pipeline:
 * tokenise → strip accents → expand synonyms (bidirectional) → relevance
 * score → diversify by category. This page only mints a per-mount shuffle
 * seed so each visit/refresh produces a fresh shuffle within the same
 * relevance band (Phase 12 contract: seed param respected by backend).
 *
 * When the smart search still returns nothing we show an empty-state with
 * the standard catalog + consultora escape hatches — same component reused
 * everywhere else on the site.
 */
export default function SearchPage() {
  const [params] = useSearchParams();
  const q = params.get("q") || "";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [consultoraOpen, setConsultoraOpen] = useState(false);

  // Fresh seed per query change AND per page mount — guarantees a new shuffle
  // every time the customer hits Search, refreshes, or returns from a PDP.
  const shuffleSeed = useMemo(
    () => Math.floor(Math.random() * 2_147_483_647),
    [q],
  );

  useEffect(() => {
    if (!q) return;
    setLoading(true);
    api
      .get("/products", { params: { search: q, seed: shuffleSeed } })
      .then((r) => {
        setProducts(r.data);
        setLoading(false);
      });
  }, [q, shuffleSeed]);

  return (
    <div className="max-w-7xl mx-auto px-6 py-16" data-testid="search-page">
      <p className="overline text-[#A8A8A8] mb-3">Busca</p>
      <h1 className="font-display text-4xl md:text-5xl mb-3">Resultados para &ldquo;{q}&rdquo;</h1>
      <p className="text-sm text-[#6B6B6B] mb-12">{products.length} peças encontradas</p>
      {loading ? (
        <p className="text-[#A8A8A8] italic">Carregando...</p>
      ) : products.length === 0 ? (
        // Empty-state — friendly fallback when even the synonym-expanded
        // smart search couldn't find anything. Offers both the catalog exit
        // and the human-touch escape hatch (same ConsultoraModal used on
        // home, Try-On result, invalid_photo, limit_reached).
        <div className="max-w-xl mx-auto text-center py-12" data-testid="search-empty-state">
          <div className="w-12 h-px bg-[#B8A88A] mx-auto mb-8"/>
          <p className="font-italic-display text-2xl text-[#1A1A1A] mb-4 leading-snug">
            Não encontramos nenhuma peça com esse termo.
          </p>
          <p className="text-sm text-[#6B6B6B] leading-relaxed mb-10">
            Que tal explorar nosso catálogo completo ou falar com uma de nossas consultoras?
          </p>
          <div className="space-y-3">
            <Link to="/joias/todos" className="btn-primary w-full" data-testid="search-empty-browse-btn">
              Ver catálogo completo
            </Link>
            <button
              type="button"
              onClick={() => setConsultoraOpen(true)}
              className="inline-block w-full border border-[#B8A88A] text-[#B8A88A] hover:bg-[#F5EFE8] hover:text-[#8C7B5E] py-3 text-xs uppercase tracking-[0.15em] transition-colors"
              style={{ fontFamily: "'Jost', sans-serif" }}
              data-testid="search-empty-consultora-cta"
            >
              Falar com uma consultora →
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
          {products.map((p) => <ProductCard key={p.id} product={p} />)}
        </div>
      )}
      <ConsultoraModal open={consultoraOpen} onOpenChange={setConsultoraOpen} />
    </div>
  );
}
